import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/types';
import { CarData, Type } from '../../../types/instant-lease-api';
import { formatGermanPrice } from '../../../utils/getter';

/*
 * Vehicle name / product label / monthly rate (Figma: MyCar - Car Overview,
 * sub-header). Mobile-first with the project breakpoints:
 *
 *                 mobile (base)         tablet (md)          desktop (lg)
 *   name          headline-200 20/24    headline-500 32/40   headline-600 40/48
 *   variant       text-loose-100 14/24  text-loose-300 18/28
 *   rate label    text-loose-50 11/20   text-loose-200 16/24
 *   rate          price-text-200 28/36  price-text-300 40/48
 *   promo-label   text-tight-50-bold    text-loose-100-bold
 *
 * The typography classes and the global `p` colour are unlayered CSS, so the
 * Tailwind overrides below need the important modifier.
 */
const textColor =
  'text-(--subheader-text,var(--vel-color-secondary-400,#0f3549))!';
const nameClass = `headline-200 m-0! md:text-[32px]! md:leading-10! lg:text-[40px]! lg:leading-12! ${textColor}`;
const variantClass = `text-loose-100 m-0! md:text-body-loose-300! ${textColor}`;
const rateLabelClass = `text-loose-50 m-0! whitespace-nowrap md:text-body-loose-200! ${textColor}`;
// Mobile: the rate overlaps the label by 4px in Figma (label 72-92, rate 88-124)
const rateClass = `price-text-200 m-0! -mt-1! whitespace-nowrap md:mt-0! md:text-price-300! ${textColor}`;
// Figma: promo-label (Kind=Brand support 3)
const promoLabelClass =
  'text-tight-50-bold inline-flex shrink-0 items-center rounded-[2px] border border-solid border-(--vel-color-semantic-info-high,#1c68ca) bg-(--vel-color-semantic-info-low,#f0f6ff) px-1 whitespace-nowrap text-(--vel-color-semantic-info-high,#1c68ca)! md:text-body-loose-100!';

interface PricingDetailsProps {
  /** Show the product label (Leasing / Auto-Abo / KIA-Flex) */
  label?: boolean;
  className?: string;
}

const PricingDetails: React.FC<PricingDetailsProps> = ({
  label = true,
  className = '',
}) => {
  // Lease Applications Store
  const { carData, activeApplication } = useSelector(
    (state: RootState) => state.leaseApp,
  );

  // handle change and update of the car data
  useEffect(() => {
    setCarDetails(carData);
  }, [carData]);

  //Car Details
  const [carDetails, setCarDetails] = useState<CarData | null | undefined>(
    carData,
  );

  // Variables
  const isCommercial = activeApplication?.type
    ? activeApplication?.type === Type.B2B
    : carDetails?.commercial;

  const vehicleData = activeApplication?.offer?.vehicle_data;

  const leasingOptions = activeApplication?.offer?.leasing_options;

  const vehicleRate = isCommercial
    ? leasingOptions?.totals?.rate?.net
    : leasingOptions?.totals?.rate?.gross;

  // Values
  const { name, brand, model, rate } = {
    name: carDetails?.carFullName || vehicleData?.full_name,
    brand: carDetails?.brand || vehicleData?.brand,
    model: carDetails?.carModel || vehicleData?.name,
    rate: vehicleRate || carDetails?.rate,
  };

  const productLabel =
    activeApplication?.partner === 'ALD_FLEX' || carData?.product === 'FLEX'
      ? 'Auto-Abo'
      : carData?.product === 'DUFLEX'
        ? 'KIA-Flex'
        : 'Leasing';

  // Format price with german price format
  const { format } = formatGermanPrice();

  return (
    <div
      className={`pricing-details flex items-start justify-between gap-4 ${className}`.trim()}
      data-testid='pricing-details'
    >
      <div className='min-w-0'>
        <div className='flex flex-wrap items-center gap-x-1 md:gap-x-2'>
          <p className={nameClass} data-testid='pricing-details-name'>
            {brand ? `${brand} ` : ''}
            {model}
          </p>
          {label && (
            <span
              className={promoLabelClass}
              data-testid='pricing-details-label'
            >
              {productLabel}
            </span>
          )}
        </div>
        <p className={variantClass} data-testid='pricing-details-variant'>
          {name}
        </p>
      </div>
      <div className='flex shrink-0 flex-col items-end text-right'>
        <p className={rateLabelClass}>
          mtl. {isCommercial ? 'exkl. MwSt. ' : 'inkl. MwSt. '}
          {carData?.product === 'DUFLEX' ? 'mieten für' : 'leasen für'}
        </p>
        <p className={rateClass} data-testid='pricing-details-rate'>
          {format(rate || 0)}
        </p>
      </div>
    </div>
  );
};

export default PricingDetails;
